import { Button } from './Button'
import { useDialogKeyboardShortcuts } from '../hooks/useDialogKeyboardShortcuts'

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  onConfirm: () => void
  onCancel: () => void
  variant?: 'danger' | 'default'
}

function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'default',
}: ConfirmDialogProps) {
  useDialogKeyboardShortcuts({
    isOpen,
    onCancel,
    onConfirm,
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-overlay transition-opacity duration-normal"
        onClick={onCancel}
        role="presentation"
        aria-hidden="true"
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
          aria-describedby="dialog-message"
          className="relative bg-surface-default rounded-lg shadow-md max-w-md w-full transform transition-all duration-normal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-5">
            {variant === 'danger' && (
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-danger-subtle mb-4">
                <svg
                  className="h-6 w-6 text-danger"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            )}
            <h3 id="dialog-title" className="text-h3 font-semibold text-text-primary text-center mb-2">{title}</h3>
            <p id="dialog-message" className="text-body text-text-secondary text-center">{message}</p>
          </div>

          <div className="px-6 py-4 bg-surface-subtle rounded-b-lg flex justify-end gap-3">
            <Button type="button" variant="secondary" primaryWeight="medium" onClick={onCancel}>
              {cancelText}
            </Button>
            <Button
              type="button"
              variant={variant === 'danger' ? 'danger' : 'primary'}
              primaryWeight="medium"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onConfirm()
              }}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConfirmDialog
